import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { withRouter, useLocation, useHistory } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { reduxForm, Field, FieldArray, formValueSelector } from 'redux-form/immutable';
import { LinearProgress, Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';

import Box from 'retired/Box';
import FormTextField from 'retired/FormTextField';
import FormRteField from 'retired/FormRteField';
import { required } from 'utils/validators';
import FormEditor from 'retired/FormEditor';
import FormFileUpload from 'retired/FormFileUpload';
import FormFieldLabel from 'retired/FormFieldLabel';
import FormCheckbox from 'retired/FormCheckbox';
import { showErrorAction } from 'containers/Notification/actions';
import { PureRender } from 'utils/component-helpers';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { imageTypes } from 'retired/FileUpload/utils';

import * as Actions from './actions';
import { FORM_IDENTIFIER } from './constants';
import { selectLoading } from './selectors';
import epics from './epics';
import reducer from './reducer';

const key = 'formFormPage';

const requiredValidator = required();

const FormFormPage = ({
  loading,
  change,
  name,
  match,
  pristine,
  invalid,
  submitting,
  actions,
  handleSubmit,
}) => {
  const location = useLocation();
  const history = useHistory();
  const isSurvey = location.pathname.startsWith('/surveys');
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    actions.initAction(match.params.id, false, isSurvey);
  }, []);

  const submit = (formData) => {
    if (pristine) {
      history.replace(`${isSurvey ? '/surveys' : '/forms'}/${match.params.id}`);
    } else {
      const data = formData.toJS();
      actions.saveAction({ ...data });
    }
  };

  const formTitle =
    match.params.id === 'new' ? (isSurvey ? 'Create New Survey' : 'Create New Form') : name;

  return (
    <>
      {loading && <LinearProgress />}
      {!loading && (
        <Box>
          <Helmet title={formTitle} />
          <h1>{formTitle}</h1>
          <form onSubmit={handleSubmit(submit)}>
            <fieldset>
              <Grid container spacing={3}>
                <Grid item md={6}>
                  <Field
                    name="name"
                    component={FormTextField}
                    fullWidth
                    autoFocus
                    inputProps={{ maxLength: 24 }}
                    label={`Name your ${isSurvey ? 'survey' : 'form'}`}
                    helperText={`${
                      isSurvey ? 'Survey' : 'Form'
                    } name will be visible to volunteers.`}
                    validate={requiredValidator}
                  />
                  <br />
                  <br />
                  <Field
                    name="subtitle"
                    component={FormTextField}
                    fullWidth
                    inputProps={{ maxLength: 24 }}
                    label="Subtitle (internal)"
                    helperText={`${
                      isSurvey ? 'Survey' : 'Form'
                    } subtitle will not be visible to volunteers.`}
                  />
                  <br />
                  <br />
                  <Field
                    name="description"
                    component={FormTextField}
                    fullWidth
                    inputProps={{ maxLength: 1000 }}
                    multiline
                    rows={3}
                    label="Description (internal)"
                    helperText={`Detailed description of the ${isSurvey ? 'survey' : 'form'}`}
                  />
                  {isSurvey && (
                    <>
                      <br />
                      <br />
                      <Field
                        name="requireLogin"
                        component={FormCheckbox}
                        label="Require the respondent to log in before filling out the survey. Only registered Deed volunteers will be able to submit a response!"
                      />
                    </>
                  )}
                </Grid>
                {!isSurvey && (
                  <Grid item lg={6}>
                    <FormFieldLabel>Document</FormFieldLabel>
                    <Field
                      id="document"
                      name="document"
                      component={FormFileUpload}
                      acceptedTypes={imageTypes}
                      maxSize={1024 * 1024 * 10}
                      imagePreview
                      allowClear
                      labelText="Have a waiver you need volunteers to sign? Simply upload an image of it here (JPEG/PNG) and volunteers will be able to provide consent within the platform. Before uploading the image, please edit the document to include any information that needs that needs to be consented to and remove check boxes or places for a signature."
                    />
                  </Grid>
                )}
              </Grid>

              <div style={{ marginTop: 30, marginBottom: 30 }}>
                <FieldArray
                  name="questions"
                  component={FormEditor}
                  change={change}
                  isSurvey={isSurvey}
                />
              </div>
              {isSurvey && (
                <div style={{ maxWidth: 1000 }}>
                  <FormFieldLabel forName="confirmationText">Confirmation text</FormFieldLabel>
                  <Field
                    name="confirmationText"
                    component={FormRteField}
                    fullWidth
                    height={100}
                    placeholder="Your response has been recorded."
                    label="Confirmation text"
                    helperText={`Confirmation message displayed after the submission of the ${
                      isSurvey ? 'survey' : 'form'
                    }`}
                  />
                  <br />
                  <br />
                </div>
              )}
              <div>
                <Button
                  type="submit"
                  disabled={
                    match.params.id === 'new'
                      ? pristine || invalid || submitting
                      : invalid || submitting
                  }
                  color="primary"
                  variant="contained"
                >
                  {match.params.id === 'new' ? 'Submit' : pristine ? 'Close' : 'Save'}
                </Button>
              </div>
            </fieldset>
          </form>
        </Box>
      )}
    </>
  );
};
FormFormPage.propTypes = {
  loading: PropTypes.bool,
  name: PropTypes.string,
  match: PropTypes.object,
  pristine: PropTypes.bool,
  invalid: PropTypes.bool,
  submitting: PropTypes.bool,
  actions: PropTypes.object,
  handleSubmit: PropTypes.func,
  change: PropTypes.func,
};

const valueSelector = formValueSelector(FORM_IDENTIFIER);

const withConnect = connect(
  (state) => ({
    name: valueSelector(state, 'name'),
    loading: selectLoading(state),
    // Force re-render on questions change
    questions: valueSelector(state, 'questions'),
  }),
  (dispatch) => ({
    actions: bindActionCreators({ ...Actions, showErrorAction }, dispatch),
  })
);

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default compose(withRouter, withConnect, withForm, PureRender)(FormFormPage);
